import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { mapStyles } from "../libs/styles";
import { CreatedEventModel, ServiceScheduleFormModel, } from "../typings/models";
import useMount from "../hooks/useMount";
import { errorUtil } from "../libs/formikError";
import LoadingButton from "@mui/lab/LoadingButton";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime, Interval } from "luxon";
import { bookService } from "../api/api";
import useSignal from "../hooks/useSignal";
import { FORM_DEFAULT_VALUES, } from "../constants/formDefaultValues";
import { validationSchema } from "../constants/validationSchema";
import { dateTimeAdapter, outputDate } from "../libs/dateAdapters";
import useScheduledEvents from "../hooks/useScheduledEvents";
import ServiceDay, { ServiceDayProps } from "./ServiceDay";
import useServiceSlots from "../hooks/useServiceSlots";
import ViewDateTime from "./ViewDateTime";
import { DATE_FORMAT, TIME_FORMAT } from "../constants/date";
import { EditCalendar } from "@mui/icons-material";

interface BookFormProps {}

const BookForm: React.FC<BookFormProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [bookedService, setBookedService] =
    useState<Nullable<CreatedEventModel>>(null);

  const signal = useSignal();

  const onSubmit = async (data: ServiceScheduleFormModel) => {
    setLoading(true);
    const res = await bookService(data, { signal });
    if (signal.aborted) return;
    setLoading(false);
    if (res.status !== "success") {
      alert(res.message ?? "Something went wrong");
      return;
    }
    setBookedService(res.data);
    resetForm();
    await mutate();
  };

  const {
    values,
    handleSubmit,
    errors,
    touched,
    isValid,
    setValues,
    setFieldValue,
    setFieldTouched,
    validateForm,
    resetForm,
  } = useFormik({
    initialValues: FORM_DEFAULT_VALUES,
    validationSchema,
    onSubmit,
  });

  const { items: scheduledEvents, isLoading: scheduledEventsIsLoading, mutate } = useScheduledEvents();

  useMount(async () => {
    await validateForm();
  });

  const scheduledIntervals = useMemo(() => {
    return scheduledEvents
      .map((bookedSlot) => {
        return Interval.fromDateTimes(DateTime.fromISO(bookedSlot.start), DateTime.fromISO(bookedSlot.end));
      });
  }, [scheduledEvents]);

  const {
    slot1,
    slot2,
    slotsAvailability,
    allEmpty,
    someBusy,
    allBusy,
  } = useServiceSlots({day: dateTimeAdapter(values.date) ?? DateTime.now(), scheduledIntervals});

  const errorHandling = errorUtil(touched, errors);

  const handleChange = async (name: string, value: any, touch?: boolean) => {
    await setFieldValue(name, value);
    if (touch) {
      await setFieldTouched(name, true);
    }
    if (name === 'date') {
      await handleChange('time', null, touch);
    }
  };

  const handleBlur = async (name: string, touch?: boolean) => {
    await setFieldTouched(name, touch);
  };

  const handleCloseDialog = () => {
    setBookedService(null);
  };

  return (
    <>
      <Box sx={styles.form} component="form" onSubmit={handleSubmit}>
        <Stack gap={1}>
          <Stack component="fieldset" sx={styles.fieldset}>
            {/*<Box component="legend">De baza</Box>*/}
            <TextField
              label="Nume"
              value={values.name}
              onChange={(ev) => handleChange("name", ev.target.value)}
              onBlur={() => handleBlur("name", true)}
              error={errorHandling.isInvalid("name")}
              required
              disabled={loading}
              sx={styles.input}
            />
            <TextField
              label="Telefon"
              value={values.phone}
              onChange={(ev) => handleChange("phone", ev.target.value)}
              onBlur={() => handleBlur("phone", true)}
              error={errorHandling.isInvalid("phone")}
              required
              disabled={loading}
              sx={styles.input}
            />
            <TextField
              label="E-mail"
              value={values.email}
              onChange={(ev) => handleChange("email", ev.target.value)}
              onBlur={() => handleBlur("email", true)}
              error={errorHandling.isInvalid("email")}
              required
              disabled={loading}
              sx={styles.input}
            />
            <DatePicker
              label="Data"
              // open
              // closeOnSelect={false}
              disablePast
              format={DATE_FORMAT}
              value={dateTimeAdapter(values.date)}
              onChange={(newValue: DateTime | null) =>
                handleChange(
                  "date",
                  newValue ? outputDate(newValue) : null,
                  true,
                )
              }
              slots={{day: ServiceDay}}
              slotProps={{
                textField: {
                  error: errorHandling.isInvalid("date"),
                  onBlur: () => handleBlur("date", true),
                  required: true,
                },
                day: {
                  scheduledIntervals,
                  scheduledEventsIsLoading,
                } as ServiceDayProps
              }}
              disabled={loading}
              sx={styles.input}
            />

            <ToggleButtonGroup
              color="primary"
              value={values.time}
              exclusive
              onChange={(e, newValue) => {
                if (!!newValue) {
                  handleChange(
                    "time",
                    newValue,
                    true,
                  )
                }
              }}
              disabled={!values.date}
              fullWidth
            >
              <ToggleButton
                value={slot1.start?.toFormat(TIME_FORMAT)!}
                disabled={slotsAvailability[0]}
              >
                {slot1.start?.toFormat(TIME_FORMAT)}
              </ToggleButton>
              <ToggleButton
                value={slot2.start?.toFormat(TIME_FORMAT)!}
                disabled={slotsAvailability[1]}
              >
                {slot2.start?.toFormat(TIME_FORMAT)}
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Vehicle"
              value={values.vehicle}
              onChange={(ev) => handleChange("vehicle", ev.target.value)}
              onBlur={() => handleBlur("vehicle", true)}
              error={errorHandling.isInvalid("vehicle")}
              required
              disabled={loading}
              sx={styles.input}
            />

            <TextField
              label="Works"
              value={values.works}
              onChange={(ev) => handleChange("works", ev.target.value)}
              onBlur={() => handleBlur("works", true)}
              error={errorHandling.isInvalid("works")}
              required
              disabled={loading}
              sx={styles.input}
            />
          </Stack>

          <Stack gap={1} direction="row" sx={styles.buttonsWrapper}>
            <LoadingButton
              type="submit"
              variant="contained"
              disabled={!isValid}
              loading={loading}
              loadingPosition="start"
              startIcon={<EditCalendar />}
              sx={styles.submit}
            >
              {loading ? "Așteptați…" : "Trimite"}
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>

      {bookedService && (
        <Dialog open onClose={handleCloseDialog}>
          <DialogTitle>Service Booked Successful</DialogTitle>
          <DialogContent>
            <Typography color="textSecondary" gutterBottom>
              {bookedService.name}<br />
              {bookedService.email}<br />
              {bookedService.comment}<br />
              <ViewDateTime value={bookedService.start} component="span" utcToLocal /><br/>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleCloseDialog}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default BookForm;

const styles = mapStyles({
  root: {},
  form: {
    width: "100%",
  },
  fieldset: {
    gap: 1.5,
    my: 1,
    p: 1,
    border: (ct) => `1px solid ${ct.palette.divider}`,
    borderRadius: 1,
  },
  input: {},
  buttonsWrapper: {
    // position: "sticky",
    // bottom: 0,
    background: (ct) => ct.palette.background.default,
    pt: 2,
    px: 2,
    mx: -2,
    borderTop: (ct) => `1px solid ${ct.palette.divider}`,
    zIndex: 1,
  },
  submit: {
    flexGrow: 1,
    minWidth: "min(25%, 100px)",
    "&.MuiLoadingButton-loading .MuiButton-icon": {
      display: "none",
    },
    "& .MuiLoadingButton-loadingIndicator": {
      position: "static",
      marginRight: 1,
      marginLeft: -0.5,
    },
  },
  documentLink: {
    lineHeight: "1em",
    minHeight: "36px",
    justifyContent: "space-between",
    width: "100%",
  },
});
